import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useCatalogRepositoryStore } from '@/store/pinia/organization/catalogRepository'
import { ObjWithPrivateProps } from '@/utils/classes'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

const DESCRIPTION_LIMIT = 200

export const state = () => ({
  detail: null,
  config: null,
  usecases: null,
  focusedVersionType: 'tag',
  errors: {
    stackConfig: [],
    stack: [],
    usecases: [],
  },
  fetchInProgress: {
    stack: false,
    stackConfig: false,
    usecases: false,
  },
  searchTerm: '',
})

export const useStackStore = defineStore('stack', {
  state,
  getters: {
    stack: (state) => state.detail,
    stackConfig: (state) => state.config,
    stackDescriptionLimit: () => DESCRIPTION_LIMIT,
    stackBelongsToCurrentOrg: (state) => {
      const orgStore = useOrganizationStore()
      return state.detail?.organization_canonical === orgStore.orgCanonical
    },
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_STACK_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_STACK_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(),

    /**
     * Start fetch operation.
     */
    START_FETCH: makeStartFetchAction(),

    /**
     * Stop fetch operation.
     */
    STOP_FETCH: makeStopFetchAction(),

    /**
     * Set search term.
     * @param {string} value
     * @returns {void}
     */
    SET_SEARCH (value) {
      this.searchTerm = value
    },

    /**
     * Set the focused version type for stack-version related components (tag|branch|all)
     * @param {string} value
     * @returns {void}
     */
    SET_FOCUSED_VERSION_TYPE (value) {
      this.focusedVersionType = value
    },

    /**
     * Set stack in state.
     * @param {Object} stack
     * @returns {void}
     */
    SET_STACK (stack) {
      stack.description = stack.description.substring(0, DESCRIPTION_LIMIT)
      this.detail = stack
    },

    /**
     * Set stack config in state.
     * @param {Object} params
     * @param {Object} params.config
     * @returns {void}
     */
    SET_STACK_CONFIG ({ config: theConfig }) {
      const config = _.cloneDeep(theConfig)

      for (const [key, useCase] of _.entries(config)) {
        if (!_.has(useCase, 'cloud_provider')) continue

        config[key] = new ObjWithPrivateProps(useCase, {
          _cloudProvider: {
            canonical: useCase.cloud_provider,
          },
        })
      }

      this.config = config
    },

    /**
     * Set stack use cases in state.
     * @param {Object} usecases
     * @returns {void}
     */
    SET_STACK_USECASES (usecases) {
      this.usecases = usecases
    },

    /**
     * Get stack by stackRef.
     * @param {Object} params
     * @param {string} params.stackRef
     * @returns {Promise<void>}
     */
    async GET_STACK ({ stackRef }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const catalogRepositoryStore = useCatalogRepositoryStore()

      this.START_FETCH('stack')
      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.getServiceCatalog(stackRef, orgStore.orgCanonical)) || {}

      if (data) {
        this.SET_STACK(data)
        const catalogRepositoryCanonical = data?.service_catalog_source_canonical
        const canGetCatalogRepository = this.$cycloid.permissions.canDisplay('GetServiceCatalogSource', catalogRepositoryCanonical)
        if (catalogRepositoryCanonical && canGetCatalogRepository) {
          await catalogRepositoryStore.GET_CATALOG_REPOSITORY({ catalogRepositoryCanonical })
        }
      }

      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })

      this.STOP_FETCH('stack')
    },

    /**
     * Get stack config.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {Object} params.queryParams
     * @returns {Promise<boolean>}
     */
    async GET_STACK_CONFIG ({ envCanonical, componentCanonical, queryParams }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS('stackConfig')
      this.START_FETCH('stackConfig')

      const { data, errors } = (await this.$cycloid.ydAPI.getComponentStackConfiguration(
        orgStore.orgCanonical,
        orgStore.projectCanonical,
        envCanonical,
        componentCanonical,
        queryParams,
      )) || {}

      if (data) this.SET_STACK_CONFIG({ config: data })
      if (errors) {
        this.SET_ERRORS({ key: 'stackConfig', errors })
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }

      this.STOP_FETCH('stackConfig')
      return !_.isEmpty(data)
    },

    /**
     * Get stack usecases.
     * @param {Object} params
     * @param {string} params.stackRef
     * @param {Object} params.queryParams
     * @returns {Promise<void>}
     */
    async GET_STACK_USECASES ({ stackRef, queryParams }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_STACK_ERRORS('usecases')
      this.START_FETCH('usecases')

      const { data, errors } = await this.$cycloid.ydAPI.getServiceCatalogTemplateUseCases(orgStore.orgCanonical, stackRef, queryParams) || {}

      if (data) this.SET_STACK_USECASES(data)
      if (errors) this.SET_ERRORS({ key: 'usecases', errors })

      this.STOP_FETCH('usecases')
    },

    /**
     * Create stack from template.
     * @param {Object} params
     * @param {Object} params.stack
     * @param {VueRouter} params.$router
     * @returns {Promise<void>}
     */
    async CREATE_STACK_FROM_TEMPLATE ({ stack, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.createServiceCatalogFromTemplate(orgStore.orgCanonical, stack)) || {}

      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })

      if (data) {
        $router.push({ name: 'stack', params: { stackRef: data.ref } })
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.created') })
      }
    },

    /**
     * Update stack.
     * @param {Object} params
     * @param {Object} params.stack
     * @returns {Promise<boolean>}
     */
    async UPDATE_STACK ({ stack }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { data, errors } = (await this.$cycloid.ydAPI.updateServiceCatalog(orgStore.orgCanonical, stack.ref, stack)) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'stack', errors })
      }

      if (data) {
        this.SET_STACK(stack)
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.updated', { stackName: stack.name }) })
      }

      return _.isEmpty(errors)
    },

    /**
     * Validate a stack's dependencies against a project and environment.
     * Returns the canonicals of the dependency stacks that are not instantiated
     * yet (empty when every dependency is met).
     * @param {Object} params
     * @param {string} params.stackRef
     * @param {string} params.projectCanonical
     * @param {string} params.environmentCanonical
     * @returns {Promise<string[]>}
     */
    async VALIDATE_STACK_DEPENDENCIES ({ stackRef, projectCanonical, environmentCanonical }) {
      const orgStore = useOrganizationStore()

      const { data } = (await this.$cycloid.ydAPI.validateServiceCatalogDependencies(
        stackRef,
        orgStore.orgCanonical,
        projectCanonical,
        environmentCanonical,
      )) || {}

      return _.get(data, 'unmet_dependencies', [])
    },

    /**
     * Delete stack.
     * @param {Object} params
     * @param {Object} params.stack
     * @param {VueRouter} params.$router
     * @returns {Promise<void>}
     */
    async DELETE_STACK ({ stack, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_STACK_ERRORS()

      const { errors } = (await this.$cycloid.ydAPI.deleteServiceCatalog(orgStore.orgCanonical, stack.ref)) || {}

      if (errors) return this.SET_ERRORS({ key: 'stack', errors })

      $router.push({ name: 'stacks' })
      this.RESET_STACK_STATE('detail')
      alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.stacks.deleted', { stackName: stack.name }) })
    },
  },
})
